import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import {
  Repeat, FileText, TrendingDown, BarChart2, Settings, Clock,
  Plus, AlertCircle, Trash2, X, CreditCard, DollarSign,
  TrendingUp, Users, CheckCircle, Calendar, Edit3, Video
} from 'lucide-react';
import api, {
  getSubscriptions, getInvoices, getExpenses, getMonthlyReport, getAITools,
  getCurrentRate, setExchangeRate, formatIQD, formatUSD, createExpense, updateExpense, getExpenseCategories, updateInvoice,
  deleteExpense, recordPayment, createInvoice, getClients, getClient,
  createClient, getClientLTV, getOverdueInvoices, createSubscription, getAllPackages,
  generateSubInvoice, getProjects, createProject, updateProject,
  updateSubscription, deleteSubscription, cancelSubscription,
  cancelInvoice, reversePayment, getInvoice,
  updateProjectStatus, archiveProject, getProjectSummary,
  getInvoiceStatement, getDailySales, getDailyDeliveries,
  getUsers, createUser, updateUser, deleteUser,
  getDeliverables, createDeliverable, deleteDeliverable, updateDeliverable,
  deleteProject, deleteInvoice, getEmployees,
  generateTrackingToken
} from '../utils/api';
import toast from 'react-hot-toast';
import SearchableSelect from './SearchableSelect';

// ─── Constants ────────────────────────────────────────────────────────────────
export const EXCHANGE_RATE = 1500; // fallback; ideally fetched from settings

// ─── Shared style primitives ──────────────────────────────────────────────────
export const S = {
  card: { background: 'var(--card-bg, white)', borderRadius: 12, border: '1px solid var(--card-border, #E2E8F0)', overflow: 'hidden' },
  cardPad: { background: 'var(--card-bg, white)', borderRadius: 12, border: '1px solid var(--card-border, #E2E8F0)', padding: 20 },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--input-border, #E2E8F0)',
    fontSize: 13, fontFamily: 'Tajawal, sans-serif', boxSizing: 'border-box', outline: 'none',
    transition: 'border-color .15s', background: 'var(--input-bg, white)', color: 'var(--text-dark-s, #374151)'
  },
  th: {
    padding: '11px 14px', textAlign: 'right', fontSize: 11, fontWeight: 700,
    color: 'var(--text-gray, #64748B)', borderBottom: '1px solid var(--border-light, #E2E8F0)', whiteSpace: 'nowrap'
  },
  sectionTitle: { margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: 'var(--text-dark-h, #0F1E33)' },
};

// ─── Tiny helpers ─────────────────────────────────────────────────────────────
export const iqd2usd = (v, rate = EXCHANGE_RATE) => (v || 0) / rate;
export const td = (extra = {}) => ({ padding: '11px 14px', fontSize: 13, ...extra });
export const pill = (bg, color) => ({
  padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
  background: bg, color, whiteSpace: 'nowrap'
});
export const btn = (bg = '#9B8EC4', color = 'white') => ({
  padding: '8px 18px', background: bg, color, border: 'none', borderRadius: 8,
  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Tajawal, sans-serif',
  display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s'
});
export const today = () => new Date().toISOString().split('T')[0];

export { SearchableSelect };

// ─── Reusable components ──────────────────────────────────────────────────────

export function Dual({ iqd, usd, bold, redIqd }) {
  // Shows IQD on one line, USD smaller below
  return (
    <div>
      <div style={{ fontWeight: bold ? 700 : 500, color: redIqd ? '#E74C3C' : undefined }}>
        {formatIQD(iqd)}
      </div>
      <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginTop: 1 }}>
        {formatUSD(usd ?? iqd2usd(iqd))}
      </div>
    </div>
  );
}

export function StatCard({ label, iqd, usd, rawValue, color = '#9B8EC4', icon, sub }) {
  const displayVal = rawValue !== undefined ? rawValue : formatIQD(iqd);
  return (
    <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
      background: 'var(--bg-flat-white, white)', borderRadius: 12, padding: '16px 20px',
      border: '1px solid var(--border-light, #E2E8F0)', position: 'relative', overflow: 'hidden'
    }}>
      {icon && (
        <div style={{
          position: 'absolute', left: 16, top: 16, opacity: 0.07,
          fontSize: 40, lineHeight: 1, color
        }}>{icon}</div>
      )}
      <div className="dark:!text-gray-400" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted, #94A3B8)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .5 }}>
        {label}
      </div>
      <div style={{ fontSize: 21, fontWeight: 800, color, lineHeight: 1.2 }}>{displayVal}</div>
      {iqd !== undefined && (
        <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginTop: 3 }}>
          {formatUSD(usd ?? iqd2usd(iqd))}
        </div>
      )}
      {sub && <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export function FilterBtn({ active, color = '#9B8EC4', onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 15px', borderRadius: 20, border: 'none', cursor: 'pointer',
      fontSize: 12, fontFamily: 'Tajawal, sans-serif', fontWeight: 600, transition: 'all .15s',
      background: active ? color : 'var(--bg-flat-100, #F1F5F9)', color: active ? 'white' : 'var(--text-gray, #64748B)'
    }}>{children}</button>
  );
}

export function Modal({ onClose, title, width = 480, children }) {
  return (
    <div
      className="dark:!bg-dark-deeper/80 dark:!backdrop-blur-sm" style={{ position: 'fixed', inset: 0, background: 'rgba(15,30,51,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-white, white)', borderRadius: 16, padding: 28, width, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', fontFamily: 'Tajawal, sans-serif', boxShadow: '0 20px 60px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h3 className="dark:!text-gray-100" style={{ margin: 0, color: 'var(--text-dark-h, #0F1E33)', fontSize: 16, fontWeight: 800 }}>{title}</h3>
          <button onClick={onClose} className="dark:!text-gray-400" style={{ background: 'var(--bg-flat-100, #F1F5F9)', border: 'none', cursor: 'pointer', color: 'var(--text-gray, #64748B)', borderRadius: 8, padding: '4px 6px', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function LabeledInput({ label, required, hint, children }) {
  return (
    <div>
      <label className="dark:!text-gray-100" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dark-s, #374151)', display: 'block', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#E74C3C' }}> *</span>}
      </label>
      {children}
      {hint && <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

export function DetailRow({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid var(--border-light, #F1F5F9)' }}>
      <span className="dark:!text-gray-400" style={{ color: 'var(--text-gray, #64748B)', fontSize: 13 }}>{label}</span>
      <span style={{ fontFamily: 'Tajawal, sans-serif', textAlign: 'left' }}>{children}</span>
    </div>
  );
}

export function EmptyRow({ cols, msg = 'لا توجد بيانات' }) {
  return <tr><td colSpan={cols} className="dark:!text-gray-400" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted, #94A3B8)', fontSize: 13 }}>{msg}</td></tr>;
}

export function DateRangeFilter({ from, to, onFrom, onTo, onClear }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input type="date" value={from} onChange={e => onFrom(e.target.value)}
        style={{ padding: '6px 9px', borderRadius: 7, border: '1.5px solid var(--border-light, #E2E8F0)', fontSize: 12, fontFamily: 'Tajawal, sans-serif', color: 'var(--text-dark-s, #374151)' }} />
      <span style={{ color: '#CBD5E1', fontSize: 12 }}>—</span>
      <input type="date" value={to} onChange={e => onTo(e.target.value)}
        style={{ padding: '6px 9px', borderRadius: 7, border: '1.5px solid var(--border-light, #E2E8F0)', fontSize: 12, fontFamily: 'Tajawal, sans-serif', color: 'var(--text-dark-s, #374151)' }} />
      {(from || to) && (
        <button onClick={onClear} className="dark:!text-gray-400" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted, #94A3B8)', padding: 2, display: 'flex' }}><X size={13} /></button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DELIVERY MODAL — shared between SubscriptionsPage + DeliverablesPage
// ═══════════════════════════════════════════════════════════════

export const VIDEO_TYPES = [
  { key: 'reels', label: 'ريلز', icon: '🎬' },
  { key: 'ugc', label: 'UGC', icon: '📱' },
  { key: 'brand', label: 'براند', icon: '🏢' },
  { key: 'story', label: 'ستوري', icon: '⚡' },
  { key: 'ai_influencer', label: 'AI Influencer', icon: '🤖' },
  { key: 'other', label: 'أخرى', icon: '📌' },
];

export function DeliveryModal({ prefilledSub, onClose, onSuccess }) {
  const today = new Date().toISOString().split('T')[0];

  // If prefilledSub — skip client/sub selection
  const [clients, setClients] = useState([]);
  const [clientSubs, setClientSubs] = useState([]);
  const [clientId, setClientId] = useState(prefilledSub ? String(prefilledSub.client_id) : '');
  const [subId, setSubId] = useState(prefilledSub ? String(prefilledSub.id) : '');
  const [activeSub, setActiveSub] = useState(prefilledSub || null);
  const [title, setTitle] = useState('');
  const [videoType, setVideoType] = useState('ugc');
  const [seconds, setSeconds] = useState('');
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [submittedToId, setSubmittedToId] = useState('');
  const [doubleXp, setDoubleXp] = useState(1);

  useEffect(() => {
    getEmployees().then(setEmployees).catch(() => { });
  }, []);

  useEffect(() => {
    if (!prefilledSub) getClients().then(setClients);
  }, [prefilledSub]);

  useEffect(() => {
    if (!prefilledSub && clientId) {
      getSubscriptions({ client_id: clientId, status: 'active' })
        .then(list => { setClientSubs(list); setSubId(''); setActiveSub(null); });
    }
  }, [clientId, prefilledSub]);

  useEffect(() => {
    if (!prefilledSub && subId) {
      const found = clientSubs.find(s => String(s.id) === subId);
      setActiveSub(found || null);
    }
  }, [subId, clientSubs, prefilledSub]);

  const remaining = activeSub ? (activeSub.remaining_seconds || 0) : 0;
  const total = activeSub ? (activeSub.total_seconds || 1) : 1;
  const secNum = parseInt(seconds) || 0;
  const remainingAfter = remaining - secNum;
  const previewPct = activeSub ? Math.min(Math.round(((total - remaining + secNum) / total) * 100), 100) : 0;

  // If client is Internal Agency (0), always allow. Otherwise check active sub remaining.
  const isInternal = clientId === '0';
  const previewOk = isInternal || remainingAfter >= 0;

  const subColor = (s) => {
    if (!s) return 'var(--text-muted, #94A3B8)';
    const pct = s.total_seconds > 0 ? (s.remaining_seconds / s.total_seconds) * 100 : 0;
    return pct > 50 ? '#17A589' : pct > 20 ? '#F59E0B' : '#E74C3C';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isInternal && !subId) return toast.error('يرجى اختيار الاشتراك');
    if (!title.trim()) return toast.error('يرجى إدخال عنوان الفيديو');
    if (!secNum || secNum <= 0) return toast.error('يرجى إدخال عدد الثواني');
    if (!isInternal && remainingAfter < 0 && !activeSub?.allow_overage) return toast.error('لا يمكنك تجاوز رصيد الثواني المتبقي للعميل، يرجى تجديد الاشتراك أولاً');

    setSaving(true);
    try {
      const res = await createDeliverable({
        client_id: Number(clientId),
        subscription_id: isInternal ? null : Number(subId),
        title: title.trim(),
        seconds_used: secNum,
        video_type: videoType,
        delivered_at: date,
        notes: notes || null,
        employee_id: employeeId === 'manager' ? null : Number(employeeId),
        submitted_to_id: (isInternal && submittedToId && submittedToId !== 'manager') ? Number(submittedToId) : null,
        employee_points_multiplier: Number(doubleXp) || 1
      });
      toast.success(res.message || 'تم تسجيل التسليم ✅');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'خطأ في التسجيل');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title="🎬 تسجيل تسليم جديد" width={520}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Client + Subscription selectors (hidden if prefilled) */}
        {!prefilledSub && (
          <>
            <LabeledInput label="العميل" required>
              <SearchableSelect
                value={clientId}
                onChange={val => { setClientId(val); setSubId(''); setActiveSub(null); }}
                options={clients.map(c => ({ value: String(c.id), label: c.name }))}
                placeholder="— اختر العميل —"
                required
                style={S.input}
              />
            </LabeledInput>
            {clientId && clientId !== '0' && (
              <LabeledInput label="الاشتراك" required>
                <select value={subId} onChange={e => setSubId(e.target.value)} required style={S.input}>
                  <option value="">— اختر الاشتراك —</option>
                  {clientSubs.map(s => (
                    <option key={s.id} value={s.id} style={{ color: subColor(s) }}>
                      {s.package_name} — متبقي {s.remaining_seconds}ث من {s.total_seconds}ث
                    </option>
                  ))}
                </select>
              </LabeledInput>
            )}
            {clientId === '0' && (
              <div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text-gray, #475569)', display: 'flex', alignItems: 'center', gap: 6 }}>
                🏢 مشروع داخلي للوكالة (لا يتطلب باقة / رصيد لا نهائي)
              </div>
            )}
          </>
        )}

        {/* Prefilled sub info */}
        {prefilledSub && (
          <div style={{ background: '#F0ECF7', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1E40AF' }}>
            <span style={{ fontWeight: 700 }}>{prefilledSub.client_name}</span>
            {' — '}{prefilledSub.package_name}
            {'  ·  '}
            <span style={{ color: subColor(prefilledSub), fontWeight: 700 }}>
              متبقي {prefilledSub.remaining_seconds}ث
            </span>
          </div>
        )}

        <LabeledInput label="عنوان الفيديو" required>
          <input value={title} onChange={e => setTitle(e.target.value)} required style={S.input} placeholder="مثال: فيديو ريلز الشهر الثالث" />
        </LabeledInput>

        <LabeledInput label="الموظف المُنفّذ" required>
          <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} required style={S.input}>
            <option value="">— اختر الموظف —</option>
            <option value="manager">المدير / داخلي (بدون بونص)</option>
            {employees.filter(emp => emp.name !== 'System (Internal)' && (emp.role !== 'team_member' || emp.team_type === 'internal') && (!emp.team_id || emp.is_team_leader || emp.team_type === 'internal')).map(emp => <option key={emp.id} value={emp.id}>{emp.name}{emp.is_team_leader ? ' (قائد فريق)' : (emp.team_type === 'internal' ? ' (عضو داخلي)' : '')}</option>)}
          </select>
        </LabeledInput>

        {/* If internal -> Show Submitted To */}
        {isInternal && (
          <LabeledInput label="مُسلَّم إلى (داخلي)" required>
            <select value={submittedToId} onChange={e => setSubmittedToId(e.target.value)} required style={S.input}>
              <option value="">— اختر الموظف أو الجهة —</option>
              <option value="manager">المدير العام</option>
              {employees.filter(emp => emp.name !== 'System (Internal)' && (emp.role !== 'team_member' || emp.team_type === 'internal') && (!emp.team_id || emp.is_team_leader || emp.team_type === 'internal')).map(emp => <option key={emp.id} value={emp.id}>{emp.name}{emp.is_team_leader ? ' (قائد فريق)' : (emp.team_type === 'internal' ? ' (عضو داخلي)' : '')}</option>)}
            </select>
          </LabeledInput>
        )}

        {/* Video type pill buttons */}
        <div>
          <div className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-gray, #64748B)', fontWeight: 600, marginBottom: 8 }}>نوع الفيديو</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {VIDEO_TYPES.map(vt => (
              <button key={vt.key} type="button"
                onClick={() => setVideoType(vt.key)}
                style={{
                  padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontFamily: 'Tajawal, sans-serif',
                  border: videoType === vt.key ? '2px solid #9B8EC4' : '1px solid var(--border-light, #E2E8F0)',
                  background: videoType === vt.key ? '#F0ECF7' : 'var(--bg-flat-white, white)',
                  color: videoType === vt.key ? '#9B8EC4' : 'var(--text-dark-s, #374151)', fontWeight: videoType === vt.key ? 700 : 400
                }}>
                {vt.icon} {vt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Seconds */}
        <div>
          <div className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-gray, #64748B)', fontWeight: 600, marginBottom: 8 }}>الثواني</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {[15, 30, 45, 60, 90].map(s => (
              <button key={s} type="button" onClick={() => setSeconds(String(s))}
                style={{
                  padding: '5px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontFamily: 'Tajawal, sans-serif',
                  border: seconds === String(s) ? '2px solid #17A589' : '1px solid var(--border-light, #E2E8F0)',
                  background: seconds === String(s) ? '#D1FAE5' : 'var(--bg-flat-white, white)',
                  color: seconds === String(s) ? '#065F46' : 'var(--text-dark-s, #374151)', fontWeight: 600
                }}>{s}ث</button>
            ))}
            <input type="number" min="1" value={seconds}
              onChange={e => setSeconds(e.target.value)}
              placeholder="أو أدخل يدوياً"
              style={{ ...S.input, width: 120, flex: 'none' }} />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, background: doubleXp > 1 ? '#FEF3C7' : 'var(--bg-flat-50, #F8FAFC)', padding: '8px 12px', borderRadius: 8, border: doubleXp > 1 ? '1px solid #F59E0B' : '1px solid var(--border-light, #E2E8F0)', transition: 'all 0.2s' }}>
            <label htmlFor="doubleXp" style={{ fontSize: 13, color: doubleXp > 1 ? '#B45309' : 'var(--text-dark-s, #374151)', fontWeight: doubleXp > 1 ? 700 : 600, display: 'flex', alignItems: 'center', gap: 6, flex: 1, cursor: 'pointer' }}>
              ⚡ مضاعف إكس بي للموظف
              <input type="number" min="1" step="0.1" id="doubleXp" value={doubleXp} onChange={e => setDoubleXp(e.target.value)} style={{ ...S.input, width: 70, padding: '4px 8px', fontSize: 13, background: 'var(--input-bg, white)', border: doubleXp > 1 ? '1px solid #F59E0B' : '1px solid var(--input-border, #E2E8F0)', outline: 'none' }} />
            </label>
            {doubleXp > 1 && <span style={{fontSize: 12, color: '#B45309', fontWeight: 600, whiteSpace: 'nowrap'}}>تفعيل البونص!</span>}
          </div>
        </div>

        {/* Live preview (only for external clients with active sub) */}
        {!isInternal && activeSub && secNum > 0 && (
          <div style={{
            background: previewOk ? '#F0FDF4' : '#FEF2F2',
            border: `1.5px solid ${previewOk ? '#86EFAC' : '#FECACA'}`,
            borderRadius: 10, padding: '12px 14px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
              <span className="dark:!text-gray-100" style={{ color: 'var(--text-dark-s, #374151)' }}>متبقي بعد التسليم:</span>
              <span style={{ fontWeight: 800, color: previewOk ? '#17A589' : '#E74C3C' }}>
                {remainingAfter}ث {!previewOk && '⚠️ تجاوز'}
              </span>
            </div>
            <div style={{ background: 'var(--bg-flat-200, #E2E8F0)', borderRadius: 99, height: 8, overflow: 'hidden' }}>
              <div style={{
                width: `${previewPct}%`, height: 8, borderRadius: 99,
                background: previewPct < 50 ? '#17A589' : previewPct < 80 ? '#F59E0B' : '#E74C3C',
                transition: 'width 0.2s'
              }} />
            </div>
            <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginTop: 4, textAlign: 'left' }}>{previewPct}% مستخدم</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <LabeledInput label="التاريخ">
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={S.input} />
          </LabeledInput>
          <LabeledInput label="ملاحظة">
            <input value={notes} onChange={e => setNotes(e.target.value)} style={S.input} placeholder="اختياري" />
          </LabeledInput>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <button type="button" onClick={onClose} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border-light, #E2E8F0)', background: 'var(--bg-flat-white, white)', cursor: 'pointer', fontSize: 13, fontFamily: 'Tajawal, sans-serif' }}>إلغاء</button>
          <button type="submit" disabled={saving || (!previewOk && !activeSub?.allow_overage && !isInternal)} style={{ ...btn('#17A589'), opacity: saving || (!previewOk && !activeSub?.allow_overage && !isInternal) ? .6 : 1, cursor: (!previewOk && !activeSub?.allow_overage && !isInternal) ? 'not-allowed' : 'pointer' }}>
            {saving ? 'جاري التسجيل...' : '✅ تسجيل التسليم'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════
// DELIVERABLES PAGE
// ═══════════════════════════════════════════════════════════════

export const VT_LABEL = {
  reels: 'ريلز', ugc: 'UGC', brand: 'براند', story: 'ستوري',
  ai_influencer: 'AI Influencer', other: 'أخرى'
};
export const VT_FILTER_TABS = [
  { key: 'all', label: 'الكل' },
  { key: 'reels', label: 'ريلز 🎬' },
  { key: 'ugc', label: 'UGC 📱' },
  { key: 'brand', label: 'براند 🏢' },
  { key: 'story', label: 'ستوري ⚡' },
  { key: 'ai_influencer', label: 'AI Influencer 🤖' },
  { key: 'other', label: 'أخرى' },
];

export function DeliveryEditModal({ deliverable, onClose, onSuccess }) {
  const [clients, setClients] = useState([]);
  const [clientSubs, setClientSubs] = useState([]);
  
  const [clientId, setClientId] = useState(String(deliverable.client_id));
  const [subId, setSubId] = useState(deliverable.subscription_id ? String(deliverable.subscription_id) : '');
  const [activeSub, setActiveSub] = useState(null);

  const [seconds, setSeconds] = useState(String(deliverable.total_seconds_used || deliverable.seconds_used || ''));
  const [employeeId, setEmployeeId] = useState(deliverable.employee_id ? String(deliverable.employee_id) : '');
  const [submittedToId, setSubmittedToId] = useState(deliverable.submitted_to_id ? String(deliverable.submitted_to_id) : '');
  const [doubleXp, setDoubleXp] = useState(deliverable.employee_points_multiplier || 1);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getEmployees().then(setEmployees).catch(() => { });
    getClients().then(setClients).catch(() => { });
  }, []);

  useEffect(() => {
    if (clientId && clientId !== '0') {
      getSubscriptions({ client_id: clientId, status: 'active' })
        .then(list => {
          // Inject old subscription if it is inactive and matches this deliverable
          if (deliverable.subscription_id && clientId === String(deliverable.client_id)) {
            const hasOld = list.some(s => String(s.id) === String(deliverable.subscription_id));
            if (!hasOld) {
              list.unshift({ id: deliverable.subscription_id, package_name: 'الاشتراك السابق (مغلق)', remaining_seconds: 0, total_seconds: 0 });
            }
          }
          setClientSubs(list);
          // Only clear subId if the client actually changed from original
          if (clientId !== String(deliverable.client_id)) {
            setSubId('');
            setActiveSub(null);
          }
        });
    } else {
      setClientSubs([]);
      setSubId('');
      setActiveSub(null);
    }
  }, [clientId, deliverable.client_id, deliverable.subscription_id]);

  useEffect(() => {
    if (subId) {
      const found = clientSubs.find(s => String(s.id) === subId);
      setActiveSub(found || null);
    }
  }, [subId, clientSubs]);

  const isInternal = clientId === '0';

  const subColor = (s) => {
    if (!s) return 'var(--text-muted, #94A3B8)';
    const pct = s.total_seconds > 0 ? (s.remaining_seconds / s.total_seconds) * 100 : 0;
    return pct > 50 ? '#17A589' : pct > 20 ? '#F59E0B' : '#E74C3C';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isInternal && !subId) return toast.error('يرجى اختيار الاشتراك');
    const secNum = parseInt(seconds, 10);
    if (!secNum || secNum <= 0) return toast.error('يرجى إدخال عدد الثواني');

    setSaving(true);
    try {
      await updateDeliverable(deliverable.id, {
        seconds_used: secNum,
        employee_id: employeeId === 'manager' || !employeeId ? null : Number(employeeId),
        submitted_to_id: submittedToId === 'manager' || !submittedToId ? null : Number(submittedToId),
        employee_points_multiplier: Number(doubleXp) || 1,
        client_id: Number(clientId),
        subscription_id: isInternal ? null : Number(subId)
      });
      toast.success('تم التعديل بنجاح');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'خطأ في التعديل');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title="✏️ تعديل التسليم" width={480}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        
        <LabeledInput label="العميل" required>
          <SearchableSelect
            value={clientId}
            onChange={val => setClientId(val)}
            options={clients.map(c => ({ value: String(c.id), label: c.name }))}
            placeholder="— اختر العميل —"
            required
            style={S.input}
          />
        </LabeledInput>
        
        {clientId && clientId !== '0' && (
          <LabeledInput label="الاشتراك" required>
            <select value={subId} onChange={e => setSubId(e.target.value)} required style={S.input}>
              <option value="">— اختر الاشتراك —</option>
              {clientSubs.map(s => (
                <option key={s.id} value={s.id} style={{ color: subColor(s) }}>
                  {s.package_name} {s.total_seconds > 0 ? `— متبقي ${s.remaining_seconds}ث من ${s.total_seconds}ث` : ''}
                </option>
              ))}
            </select>
          </LabeledInput>
        )}
        
        {clientId === '0' && (
          <div className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text-gray, #475569)', display: 'flex', alignItems: 'center', gap: 6 }}>
            🏢 مشروع داخلي للوكالة (لا يتطلب باقة / رصيد لا نهائي)
          </div>
        )}

        <LabeledInput label="عدد الثواني المسلمة" required>
          <input type="number" min="1" value={seconds} onChange={e => setSeconds(e.target.value)} required style={S.input} />
        </LabeledInput>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: doubleXp > 1 ? '#FEF3C7' : 'var(--bg-flat-50, #F8FAFC)', padding: '8px 12px', borderRadius: 8, border: doubleXp > 1 ? '1px solid #F59E0B' : '1px solid var(--border-light, #E2E8F0)', transition: 'all 0.2s', marginTop: -6 }}>
          <label htmlFor="doubleXpEdit" style={{ fontSize: 13, color: doubleXp > 1 ? '#B45309' : 'var(--text-dark-s, #374151)', fontWeight: doubleXp > 1 ? 700 : 600, display: 'flex', alignItems: 'center', gap: 6, flex: 1, cursor: 'pointer' }}>
            ⚡ مضاعف إكس بي للموظف
            <input type="number" min="1" step="0.1" id="doubleXpEdit" value={doubleXp} onChange={e => setDoubleXp(e.target.value)} style={{ ...S.input, width: 70, padding: '4px 8px', fontSize: 13, background: 'var(--input-bg, white)', border: doubleXp > 1 ? '1px solid #F59E0B' : '1px solid var(--input-border, #E2E8F0)', outline: 'none' }} />
          </label>
          {doubleXp > 1 && <span style={{fontSize: 12, color: '#B45309', fontWeight: 600, whiteSpace: 'nowrap'}}>تفعيل البونص!</span>}
        </div>

        <LabeledInput label="الموظف المُنفّذ">
          <select value={employeeId || ''} onChange={e => setEmployeeId(e.target.value)} style={S.input}>
            <option value="">— اختر الموظف —</option>
            <option value="manager">المدير / داخلي</option>
            {employees.filter(emp => emp.name !== 'System (Internal)' && (emp.role !== 'team_member' || emp.team_type === 'internal') && (!emp.team_id || emp.is_team_leader || emp.team_type === 'internal')).map(emp => <option key={emp.id} value={emp.id}>{emp.name}{emp.is_team_leader ? ' (قائد فريق)' : (emp.team_type === 'internal' ? ' (عضو داخلي)' : '')}</option>)}
          </select>
        </LabeledInput>

        <LabeledInput label="مُسلَّم إلى (الجهة المستلمة)">
          <select value={submittedToId || ''} onChange={e => setSubmittedToId(e.target.value)} style={S.input}>
            <option value="">— غير محدد —</option>
            <option value="manager">المدير العام</option>
            {employees.filter(emp => emp.name !== 'System (Internal)' && (emp.role !== 'team_member' || emp.team_type === 'internal') && (!emp.team_id || emp.is_team_leader || emp.team_type === 'internal')).map(emp => <option key={emp.id} value={emp.id}>{emp.name}{emp.is_team_leader ? ' (قائد فريق)' : (emp.team_type === 'internal' ? ' (عضو داخلي)' : '')}</option>)}
          </select>
        </LabeledInput>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
          <button type="button" onClick={onClose} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border-light, #E2E8F0)', background: 'var(--bg-flat-white, white)', cursor: 'pointer', fontSize: 13, fontFamily: 'Tajawal, sans-serif' }}>إلغاء</button>
          <button type="submit" disabled={saving} style={{ ...btn('#17A589'), opacity: saving ? .6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'جاري الحفظ...' : 'حفظ التعديلات'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
